/**
 * LANDING THEMES.
 *
 * WHY THIS IS DATA AND NOT COMPONENTS. This site is one product aimed at one
 * outcome -- better compensation -- but it is reached by different people with
 * different problems, and each of those is a separate advertising campaign with
 * its own promise. A person who typed "my boss said no to a raise" and a person
 * who typed "negotiate a job offer" must land on a page that answers THEM, or the
 * page is just noise and the click was wasted.
 *
 * So a theme is: what the page says, which guided paths it leads with, what a search engine is
 * told about it, and WHICH CAPABILITY it starts a conversation in. Adding a campaign is adding
 * an entry below -- no component changes.
 *
 * WHAT IS DELIBERATELY NOT HERE ANY MORE: the guided paths themselves, and the instruction that
 * tells the coach how to behave. Both moved to the server (`capability` table), because a
 * prompt in this bundle is a prompt a customer can read and replace, and because adding a thing
 * the product can do should not require a frontend release. A theme now names a capability; the
 * capability owns its own words.
 *
 * THE ORDER OF THE PATHS IS STILL HERE, and that is the right place for it: which situation a
 * page should lead with is a fact about the advertising campaign, not about the capability.
 */

export interface LandingTheme {
	/** Stable id; appears in the URL and is remembered for the conversation. */
	id: string;
	/** The page's <title> and its main heading. */
	headline: string;
	/** One sentence under the heading: what this page does for this person. */
	subhead: string;
	/** Three plain statements of what the customer gets. */
	pillars: string[];
	/** What a search engine is told; distinct per theme so ads are relevant. */
	metaDescription: string;
	/** Guided paths to show, most relevant first. */
	scenarioIds: string[];
	/**
	 * WHICH CAPABILITY THIS PAGE STARTS A CONVERSATION IN.
	 *
	 * Acquisition and behaviour are different concerns and this is where they meet: the theme
	 * owns the promise the page makes, and names the capability that keeps it. A page written
	 * for a new campaign therefore works the day it is added, without knowing anything about
	 * how any capability thinks -- that lives in the database.
	 */
	capability?: string;
}

export const THEMES: LandingTheme[] = [
	{
		id: 'raise',
		headline: "Let's find your next raise",
		subhead:
			"You don't need to know how to ask or negotiate. Choose where you are; we'll help you understand your leverage, prepare what to say, and practice the conversation.",
		pillars: ['Understand the situation', 'Find your leverage', 'Prepare and rehearse'],
		metaDescription:
			'Guided help to ask for a raise or promotion: work out your leverage, prepare what to say, and rehearse the conversation with your manager.',
		capability: 'salary',
		scenarioIds: ['raise', 'refused', 'rehearsal', 'leverage', 'offer'],
	},
	{
		id: 'offer',
		headline: 'Got an offer? Do not say yes yet',
		subhead:
			'Bring the offer you are actually holding. We will break down the package, find what is negotiable, and prepare what to say to HR.',
		pillars: ['Read the whole package', 'Find room to negotiate', 'Rehearse with HR'],
		metaDescription:
			'Negotiate a job offer with confidence: understand total compensation, find what is negotiable, and prepare what to say to HR.',
		capability: 'salary',
		scenarioIds: ['offer', 'leverage', 'rehearsal', 'refused', 'raise'],
	},
	{
		id: 'refused',
		headline: 'Your raise was refused. Do not resign yet',
		subhead:
			'A refusal usually answers a different question than the one you asked. Work out which one, then decide the next move with your eyes open.',
		pillars: ['Diagnose the refusal', 'Weigh your options', 'Plan the next move'],
		metaDescription:
			'Turned down for a raise? Work out whether it was budget, timing, or how your work is valued, and decide your next move deliberately.',
		capability: 'salary',
		scenarioIds: ['refused', 'leverage', 'rehearsal', 'raise', 'offer'],
	},
	{
		id: 'rehearsal',
		headline: 'Practise the conversation before it is real',
		subhead:
			'Rehearse a compensation conversation with a stand-in who pushes back. You get to make the mistakes here instead of in the room.',
		//: THE PILLARS MATCH THE PACK, NOT THE OLD FLOW. "One question at a time" and "feedback
		//: after each reply" described coaching; the practice pack puts them IN the scene and saves
		//: its observations for the end. A page that promises the shape of the interaction it is
		//: about to hand over is the whole reason these lines are server data.
		pillars: ['A realistic counterpart', 'You answer in the scene', 'Observations at the end'],
		metaDescription:
			'Rehearse a salary or raise conversation with a realistic counterpart, one question at a time, with coaching after each reply.',
		capability: 'rehearsal',
		scenarioIds: ['rehearsal', 'raise', 'offer', 'refused', 'leverage'],
	},
	{
		id: 'leverage',
		headline: 'Not sure what you are worth? Start here',
		subhead:
			'Before any number, you need evidence. We will walk through your results, scarce skills, market position, and alternatives one question at a time.',
		pillars: ['Turn results into evidence', 'Find scarce skills', 'Know your alternatives'],
		metaDescription:
			'Build the case for a higher salary: turn your results into evidence, identify scarce skills, and understand your alternatives.',
		capability: 'salary',
		scenarioIds: ['leverage', 'raise', 'offer', 'refused', 'rehearsal'],
	},
	{
		id: 'boss',
		headline: 'Work out your manager before you speak',
		subhead:
			'Describe what your manager actually says and does. We build a working model of how they operate -- with the evidence behind each conclusion -- then test how they would react to the words you plan to use.',
		pillars: ['Model how they operate', 'Test your wording', 'See the likely reaction'],
		metaDescription:
			"Work out your manager's style from what they actually say and do, then test how they would react to the wording you plan to use in a raise conversation.",
		capability: 'salary',
		scenarioIds: ['boss', 'rehearsal', 'raise', 'refused', 'leverage'],
	}
];

export const DEFAULT_THEME_ID = THEMES[0].id;

/**
 * Resolve a theme by id, falling back to the default.
 *
 * A landing page reached with an unknown or malformed id must still render a
 * coherent page: an advertising click that hits an error screen is a paid click
 * thrown away. Falling back is deliberate, not lazy.
 */
export function resolveTheme(id: string | null | undefined): LandingTheme {
	const wanted = (id ?? '').trim().toLowerCase();
	return THEMES.find((theme) => theme.id === wanted) ?? THEMES[0];
}
